import React, { ChangeEvent, ReactNode, useRef } from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  RadioGroup,
} from '@material-ui/core';
import { Controller, UseFormMethods } from 'react-hook-form';
import { get } from 'lodash';
import { useScrollIntoViewOnError } from '../../../utils/use-scroll-into-view-on-error';

export interface RHFRadioGroupProps {
  label: string;
  errors?: UseFormMethods['errors'];
  control: UseFormMethods['control'];
  name: string;
  defaultValue?: string | number;
  children: ReactNode | ReactNode[];
  onChange?: (event: ChangeEvent, value: string) => any;
  className?: string;
  required?: boolean;
  helperText?: string;
  useNullDefaultValue?: boolean;
}

export const RHFRadioGroup = ({
  children,
  label,
  control,
  name,
  defaultValue,
  onChange,
  errors,
  className,
  required,
  helperText,
  useNullDefaultValue = true,
}: RHFRadioGroupProps) => {
  const handleChange = (
    event: ChangeEvent,
    value: string,
    controllerChangeHandler: any,
  ) => {
    if (onChange) {
      onChange(event, value);
    }
    controllerChangeHandler(event, value);
  };

  const isError = !!errors && !!get(errors, name);

  const $radioGroup = useRef();
  useScrollIntoViewOnError($radioGroup.current, isError);

  function getDefaultValue() {
    if (useNullDefaultValue) {
      return {
        defaultValue: defaultValue ?? null,
      };
    }

    return defaultValue;
  }

  return (
    <FormControl className={className} error={isError}>
      <FormLabel htmlFor={name} required={required} component="legend">
        {label}
      </FormLabel>
      <Controller
        render={(props) => (
          <RadioGroup
            {...props}
            id={name}
            ref={$radioGroup}
            onChange={(event, value) =>
              handleChange(event, value, props.onChange)}
          >
            {children}
          </RadioGroup>
        )}
        name={name}
        control={control}
        {...getDefaultValue()}
      />

      {!isError && <FormHelperText>{helperText}</FormHelperText>}

      {errors && get(errors, name) && (
        <FormHelperText style={{ marginBottom: '16px' }} error>
          {get(errors, name).message}
        </FormHelperText>
      )}
    </FormControl>
  );
};
